import {
	ApolloClient,
	HttpLink,
	InMemoryCache,
	gql,
} from "@apollo/client";
import type { WPAPIPostGraphQL, WPAuthor, WPRecentPost } from "@/types";

type WordpressImageNode = {
	sourceUrl?: string | null;
	altText?: string | null;
};

type WordpressPostMeta = {
	actualPublishedDate?: string | null;
	widgetEmbed?: string | null;
};

type WordpressPost = {
	id: string;
	title: string;
	content?: string | null;
	excerpt?: string | null;
	date: string;
	slug: string;
	author?: WPAuthor | null;
	featuredImage?: {
		node?: WordpressImageNode | null;
	} | null;
	postMeta?: WordpressPostMeta | null;
};

type WordpressRecentPost = Pick<
	WordpressPost,
	| "id"
	| "title"
	| "excerpt"
	| "date"
	| "slug"
	| "author"
	| "featuredImage"
	| "postMeta"
>;

function mapPostPreview(post: WordpressPost): WPAPIPostGraphQL {
	return {
		node: {
			id: post.id,
			title: post.title,
			content: post.content ?? "",
			date: post.date,
			actual_published_date: post.postMeta?.actualPublishedDate ?? "",
			widget_embed: post.postMeta?.widgetEmbed ?? "",
			slug: post.slug,
			author:
				post.author ??
				({
					node: {
						id: "",
						name: "",
						avatar: { url: "" },
					},
				} satisfies WPAuthor),
		},
		image: {
			sourceUrl: post.featuredImage?.node?.sourceUrl || "",
			altText: post.featuredImage?.node?.altText || "",
		},
	};
}

let client: ApolloClient<unknown> | null = null;

function getWordpressGraphqlUri() {
	const uri = process.env.NEXT_PUBLIC_WORDPRESS_GRAPHQL_URI;

	if (!uri) {
		throw new Error("NEXT_PUBLIC_WORDPRESS_GRAPHQL_URI is not set");
	}

	return uri;
}

function getWordpressClient() {
	if (!client) {
		client = new ApolloClient({
			link: new HttpLink({
				uri: getWordpressGraphqlUri(),
			}),
			cache: new InMemoryCache(),
			defaultOptions: {
				query: {
					fetchPolicy: "no-cache",
					context: {
						fetchOptions: {
							next: { revalidate: 60 }, // Revalidate every 60 seconds
						},
					},
				},
			},
		});
	}

	return client;
}

const GET_POSTS_QUERY = gql`
  query GetPosts {
    posts {
      nodes {
        id
        title
        content
        date
        slug
        author {
          node {
            id
            name
            avatar {
              url
            }
          }
        }
        featuredImage {
          node {
            sourceUrl
            altText
          }
        }
        postMeta {
          actualPublishedDate
          widgetEmbed
        }
        categories {
          nodes {
            name
          }
        }
        tags {
          nodes {
            name
          }
        }
      }
    }
  }
`;

const GET_POST_BY_SLUG_QUERY = gql`
  query GetPostBySlug($slug: String!) {
    postBy(slug: $slug) {
      id
      title
      content
      date
      slug
      author {
        node {
          id
          name
          avatar {
            url
          }
        }
      }
      featuredImage {
        node {
          sourceUrl
          altText
        }
      }
      postMeta {
        actualPublishedDate
        widgetEmbed
      }
      categories {
        nodes {
          name
        }
      }
      tags {
        nodes {
          name
        }
      }
    }
  }
`;

const GET_POSTS_BY_AUTHOR_NAME_QUERY = gql`
  query GetPostsByAuthorName($authorName: String!) {
    posts(where: { authorName: $authorName }) {
      nodes {
        id
        title
        content
        date
        slug
        author {
          node {
            id
            name
            avatar {
              url
            }
          }
        }
        featuredImage {
          node {
            sourceUrl
            altText
          }
        }
        postMeta {
          actualPublishedDate
          widgetEmbed
        }
        categories {
          nodes {
            name
          }
        }
        tags {
          nodes {
            name
          }
        }
      }
    }
  }
`;

const GET_RECENT_POST_PREVIEWS = gql`
  query GetRecentPosts {
    posts(first: 9999) {
      nodes {
        id
        title
        excerpt
        date
        slug
        author {
          node {
            id
            name
            avatar {
              url
            }
          }
        }
        featuredImage {
          node {
            sourceUrl
            altText
          }
        }
        postMeta {
          actualPublishedDate
          widgetEmbed
        }
      }
    }
  }
`;

const GET_TAGS_QUERY = gql`
  query GetTags {
    tags {
      nodes {
        id
        name
        count
      }
    }
  }
`;

const GET_SITE_DATA_QUERY = gql`
  query GetSiteData {
    generalSettings {
      title
      description
    }
  }
`;

const GET_POST_BY_ID_QUERY = gql`
  query GetPostById($id: ID!) {
    post(id: $id) {
      id
      title
      content
      date
      excerpt
      slug
      author {
        node {
          id
          name
          avatar {
            url
          }
        }
      }
      featuredImage {
        node {
          sourceUrl
          altText
        }
      }
      categories {
        nodes {
          name
        }
      }
      tags {
        nodes {
          name
        }
      }
    }
  }
`;

export async function fetchPosts() {
	const { data } = await getWordpressClient().query({
		query: GET_POSTS_QUERY,
		fetchPolicy: "no-cache", // Fetch fresh data
		context: {
			fetchOptions: {
				next: { revalidate: 1 }, // Revalidate every 60 seconds
			},
		},
	});

	// Transform the data to match your structure
	return (data.posts.nodes as WordpressPost[]).map(mapPostPreview);
}

export async function fetchPostsByAuthorName(authorName: string) {
	const { data } = await getWordpressClient().query({
		query: GET_POSTS_BY_AUTHOR_NAME_QUERY,
		variables: { authorName },
		fetchPolicy: "no-cache",
	});

	// Transform the data to match your structure
	return (data.posts.nodes as WordpressPost[]).map(mapPostPreview);
}

export async function fetchPostBySlug(slug: string) {
	const { data } = await getWordpressClient().query({
		query: GET_POST_BY_SLUG_QUERY,
		variables: { slug },
		fetchPolicy: "no-cache",
		context: {
			fetchOptions: {
				next: { revalidate: 1 }, // Revalidate every 60 seconds
			},
		},
	});

	// Transform the data to match the structure in fetchPosts
	return {
		node: {
			id: data.postBy.id,
			title: data.postBy.title,
			excerpt: data.postBy.excerpt ?? "",
			content: data.postBy.content ?? "",
			date: data.postBy.date,
			actual_published_date: data.postBy.postMeta?.actualPublishedDate ?? "",
			widget_embed: data.postBy.postMeta?.widgetEmbed ?? "",
			slug: data.postBy.slug,
			author: data.postBy.author,
		},
		image: {
			sourceUrl: data.postBy.featuredImage?.node?.sourceUrl || "",
			altText: data.postBy.featuredImage?.node?.altText || "",
		},
	};
}

export async function fetchPostById(id: string) {
	const { data } = await getWordpressClient().query({
		query: GET_POST_BY_ID_QUERY,
		variables: { id },
	});

	// Transform the data to match your structure
	return {
		node: {
			id: data.post.id,
			title: data.post.title,
			content: data.post.content,
			date: data.post.date,
			slug: data.post.slug,
			author: data.post.author,
		},
		image: {
			sourceUrl: data.post.featuredImage?.node?.sourceUrl || "",
			altText: data.post.featuredImage?.node?.altText || "",
		},
	};
}

export async function fetchRecentPostPreviews() {
	const { data } = await getWordpressClient().query({
		query: GET_RECENT_POST_PREVIEWS,
		fetchPolicy: "no-cache",
		context: {
			fetchOptions: {
				next: { revalidate: 1 }, // Revalidate every 60 seconds
			},
		},
	});

	// Transform the data to match the structure with featured image
	return (data.posts.nodes as WordpressRecentPost[]).map<WPRecentPost>((post) => ({
		node: {
			id: post.id,
			title: post.title,
			excerpt: post.excerpt ?? "",
			date: post.date,
			actual_published_date: post.postMeta?.actualPublishedDate ?? "",
			widget_embed: post.postMeta?.widgetEmbed ?? "",
			slug: post.slug,
			author:
				post.author ??
				({
					node: {
						id: "",
						name: "",
						avatar: { url: "" },
					},
				} satisfies WPAuthor),
		},
		image: {
			sourceUrl: post.featuredImage?.node?.sourceUrl || "",
			altText: post.featuredImage?.node?.altText || "",
		},
	}));
}

export async function fetchTags() {
	const { data } = await getWordpressClient().query({
		query: GET_TAGS_QUERY,
	});
	return data.tags.nodes;
}

export async function fetchSiteData() {
	const { data } = await getWordpressClient().query({
		query: GET_SITE_DATA_QUERY,
	});
	return data.generalSettings;
}

export async function refetchBlogData(refetch?: boolean): Promise<void> {
	try {
		console.log("Refetching active blog queries...");

		const results =
			await getWordpressClient().refetchQueries({
				include: refetch ? "active" : [
					GET_POSTS_QUERY,
					GET_RECENT_POST_PREVIEWS,
					GET_TAGS_QUERY,
					GET_SITE_DATA_QUERY,
				],
			});

		console.log("Refetch completed successfully:");
		results.forEach((result, index) => {
			if (result.error) {
				console.warn(`Query ${index + 1} failed:`, result.error.message);
			} else {
				console.log(`Query ${index + 1} data:`, result.data);
			}
		});
	} catch (error) {
		console.error("Blog refetch failed:", error instanceof Error ? error.message : error);
	}
}

// export async function refetchBlogData(): Promise<void> {
// 	console.log("Starting refetch of blog data...");

// 	try {
// 		// Use query names as strings in the include array 
// 		const results = await client.refetchQueries({
// 			include: [
// 				"GetPosts",             // Name of GET_POSTS_QUERY
// 				"GetRecentPosts",       // Name of GET_RECENT_POST_PREVIEWS
// 				"GetTags",              // Name of GET_TAGS_QUERY
// 				"GetSiteData",          // Name of GET_SITE_DATA_QUERY
// 			],
// 		});

// 		// Log each refetch result
// 		results.forEach((result, index) => {
// 			console.log(`Refetch result ${index + 1} successful:`, result);
// 		});

// 		console.log("Refetching blog data completed.");
// 	} catch (error) {
// 		console.error("Error during refetch of blog data:", error);
// 	}
// }
